import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, doc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Search, Send, UserPlus, UserMinus, AlertTriangle, Clock, Inbox } from 'lucide-react';
import Swal from 'sweetalert2';
import { db } from '../../firebase.js';
import { useAuth } from '../../context/AuthContext.js';
import StatusBadge from '../../components/common/StatusBadge.js';
import '../../styles/SoporteAdmin.css';

const STATUS_CANONICAL_MAP = {
    abierto: 'Abierto',
    open: 'Abierto',
    'en progreso': 'En progreso',
    'en_progreso': 'En progreso',
    'en-progreso': 'En progreso',
    'in progress': 'En progreso',
    'in_progress': 'En progreso',
    respondido: 'Respondido',
    responded: 'Respondido',
    'marcado respondido': 'Respondido',
    cerrado: 'Cerrado',
    closed: 'Cerrado',
    resolved: 'Cerrado',
};

const normalizeStatus = (status) => {
    if (!status) return 'Abierto';
    const normalizedKey = status.toString().trim().toLowerCase().replace(/[_-]+/g, ' ');
    return STATUS_CANONICAL_MAP[normalizedKey] || status;
};

const STATUS_OPTIONS = [
    { value: 'Abierto', label: 'Abierto' },
    { value: 'En progreso', label: 'En progreso' },
    { value: 'Respondido', label: 'Respondido' },
    { value: 'Cerrado', label: 'Cerrado' },
];

const SLA_WARNING_MINUTES = 12 * 60;
const SLA_CRITICAL_MINUTES = 24 * 60;

const DEFAULT_FEEDBACK_TEMPLATE = 'Gracias por contactarnos. Cerramos este ticket, pero si necesitas más ayuda responde este mensaje.';

const toDate = (timestamp) => {
    if (!timestamp) return null;
    return typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
};

const minutesSince = (timestamp) => {
    const date = toDate(timestamp);
    if (!date) return Infinity;
    return Math.floor((Date.now() - date.getTime()) / 60000);
};

const formatDateTime = (timestamp) => {
    const date = toDate(timestamp);
    if (!date) return '—';
    return date.toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });
};

const slaLevel = (ticket) => {
    const minutes = minutesSince(ticket?.lastUpdate || ticket?.createdDate);
    if (minutes === Infinity) return 'neutral';
    if (minutes >= SLA_CRITICAL_MINUTES) return 'critical';
    if (minutes >= SLA_WARNING_MINUTES) return 'warning';
    return 'ok';
};

const getRequesterName = (ticket) => {
    if (!ticket) return 'Usuario';
    return ticket.userDisplayName
        || ticket.createdBy?.name
        || ticket.createdBy?.displayName
        || ticket.userEmail
        || ticket.createdBy?.email
        || ticket.userId
        || 'Usuario';
};

const getRequesterEmail = (ticket) => {
    if (!ticket) return '';
    return ticket.userEmail || ticket.createdBy?.email || '';
};

const Soporte = () => {
    const { currentUser, isAdmin, isSupport } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('todos');
    const [assignmentFilter, setAssignmentFilter] = useState('todos');
    const [priorityFilter, setPriorityFilter] = useState('todos');
    const [ticketsLoading, setTicketsLoading] = useState(true);
    const [ticketsError, setTicketsError] = useState(null);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const bottomRef = useRef(null);
    const [userInsight, setUserInsight] = useState(null);
    const [userInsightLoading, setUserInsightLoading] = useState(false);
    const [userInsightError, setUserInsightError] = useState(null);
    const [activeDetailTab, setActiveDetailTab] = useState('conversation');
    const [incomingTicketCount, setIncomingTicketCount] = useState(0);
    const knownTicketIdsRef = useRef(new Set());
    const initialLoadRef = useRef(true);
    const [overdueCount, setOverdueCount] = useState(0);
    const [slaAlertDismissedAt, setSlaAlertDismissedAt] = useState(null);
    const [internalNotes, setInternalNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editingMessageText, setEditingMessageText] = useState('');
    const [urgentTicketsCount, setUrgentTicketsCount] = useState(0);
    const [pendingResponseCount, setPendingResponseCount] = useState(0);

    const functions = useMemo(() => getFunctions(undefined, 'us-central1'), []);
    const apiCallable = useMemo(() => httpsCallable(functions, 'api'), [functions]);

    const canManage = Boolean(currentUser) && (isAdmin || isSupport);

    useEffect(() => {
        if (!canManage) {
            setTickets([]);
            setTicketsLoading(false);
            return;
        }

        // Solicitar permisos de notificación para soporte
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    console.log('Permisos de notificación concedidos para soporte');
                }
            });
        }

        setTicketsLoading(true);
        setTicketsError(null);

        const ticketsRef = collection(db, 'support_tickets');
        const q = query(ticketsRef, orderBy('lastUpdate', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const snapshotTickets = snapshot.docs.map((docSnap) => {
                const data = docSnap.data();
                return {
                    id: docSnap.id,
                    ...data,
                    status: normalizeStatus(data.status),
                };
            });
            setTickets(snapshotTickets);
            setTicketsLoading(false);

            if (initialLoadRef.current) {
                snapshot.docs.forEach((docSnap) => knownTicketIdsRef.current.add(docSnap.id));
                initialLoadRef.current = false;
                return;
            }

            const freshAdds = snapshot.docChanges().filter((change) => change.type === 'added' && !change.doc.metadata.hasPendingWrites);
            if (freshAdds.length === 0) {
                return;
            }

            let newCount = 0;
            freshAdds.forEach((change) => {
                if (!knownTicketIdsRef.current.has(change.doc.id)) {
                    knownTicketIdsRef.current.add(change.doc.id);
                    newCount += 1;

                    // Notificación push para tickets nuevos
                    const ticketData = change.doc.data();
                    if ('Notification' in window && Notification.permission === 'granted') {
                        const notification = new Notification('Nuevo Ticket de Soporte', {
                            body: `${ticketData.subject || 'Nuevo ticket'} - ${ticketData.userEmail || 'Usuario'}`,
                            icon: '/favicon.ico',
                            tag: 'support-ticket',
                            requireInteraction: true,
                            silent: false // Habilitar sonido
                        });

                        notification.onclick = () => {
                            window.focus();
                            // Aquí podríamos seleccionar el ticket automáticamente
                            notification.close();
                        };

                        // Auto-cerrar después de 15 segundos para tickets normales, 30 para urgentes
                        const isUrgent = ticketData.priority === 'high' || (ticketData.createdDate && minutesSince(ticketData.createdDate) > SLA_WARNING_MINUTES);
                        setTimeout(() => notification.close(), isUrgent ? 30000 : 15000);
                    }

                    // Reproducir sonido si está disponible
                    try {
                        const audio = new Audio('/sounds/notification.mp3');
                        audio.volume = 0.3;
                        audio.play().catch(() => {
                            // Silenciar errores de audio
                        });
                    } catch (e) {
                        // Silenciar errores de audio
                    }
                }
            });

            if (newCount > 0) {
                setIncomingTicketCount((prev) => prev + newCount);
            }
        }, (err) => {
            console.error('Error fetching tickets in realtime:', err);
            setTicketsError('No pudimos cargar los tickets. Verifica tu conexión o permisos.');
            setTicketsLoading(false);
        });

        return () => unsubscribe();
    }, [canManage]);

    const selectedTicket = useMemo(
        () => tickets.find(ticket => ticket.id === selectedTicketId) || null,
        [tickets, selectedTicketId]
    );

    const selectedTicketStatus = selectedTicket ? normalizeStatus(selectedTicket.status) : 'Abierto';
    const selectedRequesterName = useMemo(() => getRequesterName(selectedTicket), [selectedTicket]);
    const selectedRequesterEmail = useMemo(() => getRequesterEmail(selectedTicket), [selectedTicket]);
    const currencyFormatter = useMemo(() => new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
    }), []);
    const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));
    const formatProfileDate = (value) => {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    const formatProgressPercent = (value) => {
        if (value === null || value === undefined) return '—';
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) return '—';
        const bounded = Math.min(200, Math.max(0, numericValue));
        return `${bounded.toFixed(1)}%`;
    };
    const insightProfile = userInsight?.profile || {};
    const insightSummary = userInsight?.summary || {
        totalInvested: 0,
        activeAgreements: 0,
        totalAgreements: 0,
        acceleratorAgreements: 0,
        withdrawableBalance: 0,
    };
    const insightAgreements = userInsight?.agreements || [];
    const insightReferrals = userInsight?.referrals || { summary: null, direct: [] };
    const referralSummary = insightReferrals?.summary || null;
    const referralList = insightReferrals?.direct || [];
    const commissionSummary = userInsight?.commissionSummary || null;
    const commissionAllocations = commissionSummary?.allocations || {};
    const hasReferralData = (referralSummary?.totalDirect || 0) > 0 || referralList.length > 0;
    const hasCommissionData = Boolean(commissionSummary && commissionSummary.totalCredits > 0);
    const profileFieldConfigs = [
        { key: 'fullName', label: 'Nombre completo', value: insightProfile.displayName || '—', always: true },
        { key: 'teamName', label: 'Equipo', value: insightProfile.teamName || null },
        { key: 'lastLogin', label: 'Último ingreso', value: insightProfile.lastLoginAt ? formatProfileDate(insightProfile.lastLoginAt) : null },
        { key: 'smallOwner', label: 'Pequeño dueño', value: insightProfile.isSmallOwner ? 'Sí' : 'No', always: true },
        { key: 'acceleratorStatus', label: 'Estado de acelerador', value: insightProfile.acceleratorStatus || '—', always: true },
    ];

    useEffect(() => {
        let cancelled = false;
        const fetchUserInsight = async () => {
            if (!apiCallable || !selectedTicket?.userId) {
                setUserInsight(null);
                setUserInsightError(null);
                return;
            }
            setUserInsightLoading(true);
            setUserInsightError(null);
            try {
                const response = await apiCallable({
                    __fn: 'getSupportUserSnapshot',
                    userId: selectedTicket.userId,
                });
                if (!cancelled) {
                    setUserInsight(response?.data || null);
                }
            } catch (error) {
                console.error('Error obteniendo la ficha del usuario:', error);
                if (!cancelled) {
                    setUserInsight(null);
                    setUserInsightError('No pudimos cargar la información del usuario.');
                }
            } finally {
                if (!cancelled) {
                    setUserInsightLoading(false);
                }
            }
        };

        fetchUserInsight();
        return () => {
            cancelled = true;
        };
    }, [apiCallable, selectedTicket?.userId]);

    useEffect(() => {
        if (!selectedTicket) {
            setMessages([]);
            setInternalNotes([]);
            setActiveDetailTab('conversation');
            return;
        }

        setMessagesLoading(true);
        const messagesRef = collection(db, 'support_tickets', selectedTicket.id, 'messages');
        const q = query(messagesRef, orderBy('createdAt', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const snapshotMessages = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
            setMessages(snapshotMessages);
            setMessagesLoading(false);
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }, (err) => {
            console.error('Error fetching ticket messages in realtime:', err);
            setMessagesLoading(false);
        });

        return () => unsubscribe();
    }, [selectedTicket]);

    useEffect(() => {
        const criticalTickets = tickets.filter((ticket) => ticket.status !== 'Cerrado' && slaLevel(ticket) === 'critical');
        setOverdueCount(criticalTickets.length);

        // Nuevos contadores para mejor visibilidad
        const urgent = tickets.filter((ticket) => 
            ticket.status !== 'Cerrado' && 
            (slaLevel(ticket) === 'critical' || ticket.priority === 'high')
        );
        setUrgentTicketsCount(urgent.length);

        const pendingResponse = tickets.filter((ticket) => 
            ticket.status === 'Abierto' || ticket.status === 'Respondido'
        );
        setPendingResponseCount(pendingResponse.length);
    }, [tickets]);

    useEffect(() => {
        if (!selectedTicket) {
            return;
        }
        setNotesLoading(true);
        const notesRef = collection(db, 'support_tickets', selectedTicket.id, 'internal_notes');
        const q = query(notesRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setInternalNotes(snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })));
            setNotesLoading(false);
        }, (err) => {
            console.error('Error fetching internal notes:', err);
            setNotesLoading(false);
        });

        return () => unsubscribe();
    }, [selectedTicket]);

    const filteredTickets = useMemo(() => {
        let result = tickets;
        const term = searchTerm.trim().toLowerCase();

        if (statusFilter !== 'todos') {
            result = result.filter(ticket => normalizeStatus(ticket.status).toLowerCase() === statusFilter.toLowerCase());
        }

        if (assignmentFilter === 'me' && currentUser) {
            result = result.filter(ticket => ticket.assignedTo?.uid === currentUser.uid);
        }

        if (assignmentFilter === 'unassigned') {
            result = result.filter(ticket => !ticket.assignedTo || !ticket.assignedTo.uid);
        }

        if (priorityFilter === 'high') {
            result = result.filter(ticket => ticket.priority === 'high' || slaLevel(ticket) === 'critical');
        }

        if (priorityFilter === 'urgent') {
            result = result.filter(ticket => slaLevel(ticket) === 'critical');
        }

        if (term) {
            result = result.filter(ticket => {
                const subject = (ticket.subject || '').toLowerCase();
                const email = getRequesterEmail(ticket).toLowerCase();
                const requesterName = getRequesterName(ticket).toLowerCase();
                const department = (ticket.department || '').toLowerCase();
                const userId = (ticket.userId || '').toLowerCase();
                return subject.includes(term)
                    || email.includes(term)
                    || requesterName.includes(term)
                    || department.includes(term)
                    || ticket.id.toLowerCase().includes(term)
                    || userId.includes(term);
            });
        }

        return result;
    }, [tickets, statusFilter, assignmentFilter, priorityFilter, searchTerm, currentUser]);

    // --- OPTIMIZACIÓN: Memoizar tickets procesados para la UI ---
    const processedTickets = useMemo(() => {
        return filteredTickets.map(ticket => {
            const normalizedStatus = normalizeStatus(ticket.status);
            return {
                ...ticket,
                // Datos pre-calculados para el renderizado
                requesterLabel: getRequesterName(ticket),
                requesterEmail: getRequesterEmail(ticket),
                sla: slaLevel(ticket),
                normalizedStatus: normalizedStatus,
                statusKey: normalizedStatus.toLowerCase().replace(/\s+/g, '-'),
                responseLabel: {
                    'Abierto': 'Pendiente de respuesta',
                    'En progreso': 'Atendiéndose',
                    'Respondido': 'Esperando confirmación del cliente',
                    'Cerrado': 'Ticket cerrado',
                }[normalizedStatus] || normalizedStatus,
            };
        });
    }, [filteredTickets]);

    const ticketCounters = useMemo(() => {
        return tickets.reduce((acc, ticket) => {
            const normalized = normalizeStatus(ticket.status);
            const key = normalized || 'Abierto';
            acc[key] = (acc[key] || 0) + 1;
            if (!ticket.assignedTo || !ticket.assignedTo.uid) {
                acc.sinAsignar = (acc.sinAsignar || 0) + 1;
            }
            if (slaLevel(ticket) === 'critical') {
                acc.vencidos = (acc.vencidos || 0) + 1;
            }
            return acc;
        }, { total: tickets.length });
    }, [tickets]);

    const handleSelectTicket = (ticketId) => {
        setSelectedTicketId(ticketId);
    };

    const acknowledgeIncomingTickets = () => {
        setIncomingTicketCount(0);
    };

    const handleSendMessage = async (event) => {
        event.preventDefault();
        if (!selectedTicket || !newMessage.trim() || !currentUser) return;

        setIsSending(true);
        try {
            const ticketRef = doc(db, 'support_tickets', selectedTicket.id);
            await addDoc(collection(ticketRef, 'messages'), {
                sender: 'admin',
                authorUid: currentUser.uid,
                authorEmail: currentUser.email || 'admin@support',
                text: newMessage.trim(),
                createdAt: serverTimestamp(),
            });

            const nextStatus = selectedTicketStatus === 'Cerrado' ? 'Cerrado' : 'Respondido';

            await updateDoc(ticketRef, {
                status: nextStatus,
                lastUpdate: serverTimestamp(),
            });

            setNewMessage('');
            setTickets((prevTickets) => prevTickets.map((ticket) => (
                ticket.id === selectedTicket.id
                    ? { ...ticket, status: nextStatus }
                    : ticket
            )));
        } catch (error) {
            console.error('Error sending support message:', error);
            Swal.fire('Error', 'No logramos enviar tu respuesta. Intenta nuevamente.', 'error');
        } finally {
            setIsSending(false);
        }
    };

    const handleStatusChange = async (statusValue) => {
        if (!selectedTicket) return;
        const nextStatus = normalizeStatus(statusValue);
        if (selectedTicketStatus === nextStatus) {
            return;
        }

        try {
            await updateDoc(doc(db, 'support_tickets', selectedTicket.id), {
                status: nextStatus,
                lastUpdate: serverTimestamp(),
            });
            setTickets((prevTickets) => prevTickets.map((ticket) => (
                ticket.id === selectedTicket.id
                    ? { ...ticket, status: nextStatus }
                    : ticket
            )));
        } catch (error) {
            console.error('Error updating ticket status:', error);
            Swal.fire('Error', 'No se pudo actualizar el estado del ticket.', 'error');
        }
    };

    const handleAssignToMe = async () => {
        if (!selectedTicket || !currentUser) return;

        try {
            const nextStatus = selectedTicketStatus === 'Abierto' ? 'En progreso' : selectedTicketStatus;

            await updateDoc(doc(db, 'support_tickets', selectedTicket.id), {
                assignedTo: {
                    uid: currentUser.uid,
                    email: currentUser.email || '',
                    displayName: currentUser.displayName || '',
                },
                status: nextStatus,
                lastUpdate: serverTimestamp(),
            });
            setTickets((prevTickets) => prevTickets.map((ticket) => (
                ticket.id === selectedTicket.id
                    ? { ...ticket, status: nextStatus }
                    : ticket
            )));
        } catch (error) {
            console.error('Error assigning ticket:', error);
            Swal.fire('Error', 'No pudimos asignarte el ticket.', 'error');
        }
    };

    const handleUnassign = async () => {
        if (!selectedTicket) return;

        try {
            const nextStatus = selectedTicketStatus === 'En progreso' ? 'Abierto' : selectedTicketStatus;

            await updateDoc(doc(db, 'support_tickets', selectedTicket.id), {
                assignedTo: null,
                status: nextStatus,
                lastUpdate: serverTimestamp(),
            });
            setTickets((prevTickets) => prevTickets.map((ticket) => (
                ticket.id === selectedTicket.id
                    ? { ...ticket, status: nextStatus }
                    : ticket
            )));
        } catch (error) {
            console.error('Error unassigning ticket:', error);
            Swal.fire('Error', 'No se pudo quitar la asignación.', 'error');
        }
    };

    // Editar mensaje
    const handleEditMessage = (msg) => {
        setEditingMessageId(msg.id);
        setEditingMessageText(msg.text);
    };
    const handleEditMessageChange = (e) => setEditingMessageText(e.target.value);
    const handleEditMessageSave = async (msg) => {
        if (!selectedTicket || !editingMessageText.trim()) return;
        try {
            await updateDoc(doc(db, 'support_tickets', selectedTicket.id, 'messages', msg.id), {
                text: editingMessageText.trim(),
                editedAt: serverTimestamp(),
            });
            setEditingMessageId(null);
            setEditingMessageText('');
        } catch (err) {
            alert('No se pudo editar el mensaje.');
        }
    };
    const handleEditMessageCancel = () => {
        setEditingMessageId(null);
        setEditingMessageText('');
    };
    // Eliminar mensaje
    const handleDeleteMessage = async (msg) => {
        if (!selectedTicket) return;
        if (!window.confirm('¿Seguro que deseas eliminar este mensaje?')) return;
        try {
            await deleteDoc(doc(db, 'support_tickets', selectedTicket.id, 'messages', msg.id));
        } catch (err) {
            alert('No se pudo eliminar el mensaje.');
        }
    };

    const handleTogglePriority = async () => {
        if (!selectedTicket) return;

        const newPriority = selectedTicket.priority === 'high' ? 'normal' : 'high';

        try {
            await updateDoc(doc(db, 'support_tickets', selectedTicket.id), {
                priority: newPriority,
                lastUpdate: serverTimestamp(),
            });
            setTickets((prevTickets) => prevTickets.map((ticket) => (
                ticket.id === selectedTicket.id
                    ? { ...ticket, priority: newPriority }
                    : ticket
            )));

            Swal.fire('Éxito', `Ticket marcado como prioridad ${newPriority === 'high' ? 'alta' : 'normal'}`, 'success');
        } catch (error) {
            console.error('Error updating priority:', error);
            Swal.fire('Error', 'No se pudo actualizar la prioridad.', 'error');
        }
    };

    const renderTicketRow = (ticket) => {
        const isActive = selectedTicket?.id === ticket.id;
        // Ahora usamos los datos pre-calculados
        const { requesterLabel, requesterEmail, sla, statusKey, responseLabel } = ticket;
        
        // Determinar si es urgente
        const isUrgent = sla === 'critical' || ticket.priority === 'high';
        const isUnassigned = !ticket.assignedTo || !ticket.assignedTo.uid;
        const needsAttention = ticket.status === 'Abierto' || ticket.status === 'Respondido';

        return (
            <button
                key={ticket.id}
                type="button"
                className={`support-ticket-card ${isActive ? 'support-ticket-card--active' : ''} support-ticket-card--status-${statusKey} ${isUrgent ? 'support-ticket-card--urgent' : ''} ${needsAttention ? 'support-ticket-card--attention' : ''}`}
                data-status={statusKey}
                onClick={() => handleSelectTicket(ticket.id)}
            >
                <div className="support-ticket-card__title">
                    <span className="support-ticket-card__subject">
                        {isUrgent && '🚨 '}
                        {isUnassigned && '👤 '}
                        {ticket.subject || 'Sin asunto'}
                    </span>
                    <div className="support-ticket-card__indicators">
                        <span className={`support-ticket-card__sla support-ticket-card__sla--${sla}`}>
                            <Clock size={14} />
                            {sla === 'critical' && 'VENCIDO'}
                            {sla === 'warning' && 'Riesgo'}
                            {sla === 'ok' && 'OK'}
                            {sla === 'neutral' && '—'}
                        </span>
                        {ticket.priority === 'high' && (
                            <span className="support-ticket-card__priority">ALTA</span>
                        )}
                    </div>
                </div>
                <div className="support-ticket-card__meta">
                    <span className="support-ticket-card__email">
                        {/* <Mail size={14} /> */}
                        {requesterLabel}
                        {requesterEmail && requesterEmail.toLowerCase() !== requesterLabel.toLowerCase() && (
                            <span className="support-ticket-card__email-secondary"> · {requesterEmail}</span>
                        )}
                    </span>
                    <div className="support-ticket-card__status">
                        <span className={`support-ticket-card__status-label status-label--${statusKey}`}>
                            {needsAttention && '⚡ '}
                            {responseLabel}
                        </span>
                        <StatusBadge status={ticket.status || 'Abierto'} variant="table" />
                    </div>
                </div>
                <div className="support-ticket-card__footer">
                    <span className="support-ticket-card__assigned">
                        {isUnassigned ? (
                            <span className="support-ticket-card__unassigned">Sin asignar</span>
                        ) : (
                            <>Asignado a: {ticket.assignedTo?.email || 'Desconocido'}</>
                        )}
                    </span>
                    <span className="support-ticket-card__date">{formatDateTime(ticket.lastUpdate || ticket.createdDate)}</span>
                </div>
            </button>
        );
    };

    if (!canManage) {
        return (
            <div className="main-panel">
                <div className="support-empty-state">
                    <AlertTriangle size={32} />
                    <p>No tienes permisos para acceder al módulo de soporte.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="main-panel support-panel">
            <header className="support-header">
                <div className="support-header__titles">
                    <h2 className="section-title">Gestión de Soporte</h2>
                    <p className="support-header__subtitle">Organiza, asigna y responde a los tickets de los usuarios en tiempo real.</p>
                </div>
                <div className="support-header__metrics">
                    <div className="support-metric">
                        <Inbox size={16} />
                        <span>Total</span>
                        <strong>{ticketCounters.total || 0}</strong>
                    </div>
                    <div className={`support-metric ${pendingResponseCount > 0 ? 'support-metric--warning' : ''}`}>
                        <Clock size={16} />
                        <span>Pendientes</span>
                        <strong>{pendingResponseCount || 0}</strong>
                    </div>
                    <div className={`support-metric ${urgentTicketsCount > 0 ? 'support-metric--urgent' : ''}`}>
                        <AlertTriangle size={16} />
                        <span>Urgentes</span>
                        <strong>{urgentTicketsCount || 0}</strong>
                    </div>
                    <div className={`support-metric ${ticketCounters.vencidos > 0 ? 'support-metric--alert' : ''}`}>
                        <AlertTriangle size={16} />
                        <span>Vencidos</span>
                        <strong>{ticketCounters.vencidos || 0}</strong>
                    </div>
                </div>
            </header>

            {incomingTicketCount > 0 && (
                <div className="support-new-alert support-new-alert--urgent" role="status">
                    <div className="support-new-alert__content">
                        <AlertTriangle size={20} />
                        <div>
                            <strong>{incomingTicketCount} ticket{incomingTicketCount > 1 ? 's' : ''} nuevo{incomingTicketCount > 1 ? 's' : ''}</strong>
                            <p>Revisa la lista y asigna los tickets pendientes</p>
                        </div>
                    </div>
                    <button type="button" onClick={acknowledgeIncomingTickets}>
                        Entendido
                    </button>
                </div>
            )}

            {urgentTicketsCount > 0 && overdueCount === 0 && (
                <div className="support-new-alert support-new-alert--warning" role="status">
                    <div className="support-new-alert__content">
                        <Clock size={20} />
                        <div>
                            <strong>{urgentTicketsCount} ticket{urgentTicketsCount > 1 ? 's' : ''} requiere{urgentTicketsCount > 1 ? 'n' : ''} atención</strong>
                            <p>Tickets de alta prioridad o en riesgo de vencer</p>
                        </div>
                    </div>
                </div>
            )}

            {overdueCount > 0 && (
                <div className="support-new-alert support-new-alert--critical" role="status">
                    <div className="support-new-alert__content">
                        <AlertTriangle size={20} />
                        <div>
                            <strong>¡{overdueCount} ticket{overdueCount > 1 ? 's' : ''} VENCIDO{overdueCount > 1 ? 'S' : ''}!</strong>
                            <p>Estos tickets excedieron el tiempo límite de respuesta</p>
                        </div>
                    </div>
                    {!slaAlertDismissedAt && (
                        <button 
                            type="button" 
                            onClick={() => setSlaAlertDismissedAt(Date.now())}
                            className="support-new-alert__dismiss"
                        >
                            Ocultar
                        </button>
                    )}
                </div>
            )}

            <div className="support-layout">
                <aside className="support-sidebar">
                    <div className="support-search">
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="Buscar por asunto, correo o ID"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </div>

                    <div className="support-filters">
                        <label>
                            Estado
                            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                                <option value="todos">Todos</option>
                                {STATUS_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Asignación
                            <select value={assignmentFilter} onChange={(event) => setAssignmentFilter(event.target.value)}>
                                <option value="todos">Todos</option>
                                <option value="me">Asignados a mí</option>
                                <option value="unassigned">Sin asignar</option>
                            </select>
                        </label>
                        <label>
                            Prioridad
                            <select value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)}>
                                <option value="todos">Todos</option>
                                <option value="urgent">Solo críticos</option>
                                <option value="high">Altos + críticos</option>
                            </select>
                        </label>
                    </div>

                    <div className="support-ticket-list">
                        {ticketsLoading && <div className="support-ticket-placeholder">Cargando tickets...</div>}
                        {ticketsError && <div className="support-ticket-error">{ticketsError}</div>}
                        {!ticketsLoading && !ticketsError && processedTickets.length === 0 && (
                            <div className="support-ticket-placeholder">No encontramos tickets con esos filtros.</div>
                        )}
                        {!ticketsLoading && !ticketsError && processedTickets.map(renderTicketRow)}
                    </div>
                </aside>

                <section className="support-conversation">
                    {!selectedTicket && (
                        <div className="support-empty-state">
                            <Inbox size={36} />
                            <p>Selecciona un ticket para ver la conversación.</p>
                        </div>
                    )}

                    {selectedTicket && (
                        <>
                            <header className="support-conversation__header">
                                <div className="support-conversation__info">
                                    <h3>{selectedTicket.subject || 'Ticket sin asunto'}</h3>
                                    <p>
                                        {selectedRequesterName}
                                        {selectedRequesterEmail && selectedRequesterEmail.toLowerCase() !== selectedRequesterName.toLowerCase() && ` · ${selectedRequesterEmail}`}
                                        {` · ${formatDateTime(selectedTicket.createdDate)}`}
                                    </p>
                                    <div className="support-conversation__tags">
                                        {selectedTicket.department && <span className="support-tag">Depto: {selectedTicket.department}</span>}
                                        {selectedTicket.priority && <span className="support-tag">Prioridad: {selectedTicket.priority}</span>}
                                    </div>
                                </div>
                                <div className="support-conversation__actions">
                                    <div className="support-assignment">
                                        <span className="support-assignment__label">Asignado a:</span>
                                        <strong>{selectedTicket.assignedTo?.email || 'Sin asignar'}</strong>
                                    </div>
                                    <div className="support-conversation__buttons">
                                        <StatusBadge status={selectedTicketStatus} />
                                        <button 
                                            type="button" 
                                            className={`support-btn ${selectedTicket.priority === 'high' ? 'support-btn--high-priority' : ''}`}
                                            onClick={handleTogglePriority}
                                            title={selectedTicket.priority === 'high' ? 'Quitar prioridad alta' : 'Marcar como alta prioridad'}
                                        >
                                            <AlertTriangle size={16} /> 
                                            {selectedTicket.priority === 'high' ? 'Alta' : 'Normal'}
                                        </button>
                                        {(!selectedTicket.assignedTo || selectedTicket.assignedTo.uid !== currentUser?.uid) && (
                                            <button type="button" className="support-btn" onClick={handleAssignToMe}>
                                                <UserPlus size={16} /> Asignarme
                                            </button>
                                        )}
                                        {selectedTicket.assignedTo?.uid === currentUser?.uid && (
                                            <button type="button" className="support-btn" onClick={handleUnassign}>
                                                <UserMinus size={16} /> Liberar
                                            </button>
                                        )}
                                        <select
                                            value={selectedTicketStatus}
                                            onChange={(event) => handleStatusChange(event.target.value)}
                                        >
                                            {STATUS_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </header>

                            <div className="support-detail-tabs">
                                <button
                                    type="button"
                                    className={`support-detail-tab ${activeDetailTab === 'conversation' ? 'is-active' : ''}`}
                                    onClick={() => setActiveDetailTab('conversation')}
                                >
                                    Conversación
                                </button>
                                <button
                                    type="button"
                                    className={`support-detail-tab ${activeDetailTab === 'insight' ? 'is-active' : ''}`}
                                    onClick={() => setActiveDetailTab('insight')}
                                >
                                    Perfil del Usuario
                                </button>
                            </div>

                            {activeDetailTab === 'conversation' && (
                                <div className="support-detail-panel support-detail-panel--conversation">
                                    <div className="support-message-list">
                                        {messagesLoading && <div className="support-ticket-placeholder">Cargando mensajes...</div>}
                                        {!messagesLoading && messages.length === 0 && (
                                            <div className="support-ticket-placeholder">Sin mensajes registrados.</div>
                                        )}
                                        {!messagesLoading && messages.map(message => {
                                            const isAgent = message.sender !== 'user';
                                            const isOwnMessage = message.authorUid === currentUser?.uid; // Solo el propio usuario puede editar
                                            const isEditing = editingMessageId === message.id;
                                            return (
                                                <div
                                                    key={message.id}
                                                    className={`support-message ${isAgent ? 'support-message--agent' : 'support-message--user'}`}
                                                >
                                                    <div className="support-message__author">
                                                        {isAgent ? 'Equipo de Soporte' : selectedRequesterName}
                                                        <span>{formatDateTime(message.createdAt)}{message.editedAt && ' (editado)'}</span>
                                                    </div>
                                                    {isEditing ? (
                                                        <div className="support-message-edit-box">
                                                            <textarea
                                                                value={editingMessageText}
                                                                onChange={handleEditMessageChange}
                                                                style={{width:'100%', minHeight: '60px', fontSize: '1rem'}}
                                                                autoFocus
                                                            />
                                                            <div style={{display:'flex', gap:8, marginTop:8}}>
                                                                <button type="button" className="support-btn" onClick={() => handleEditMessageSave(message)}>Guardar</button>
                                                                <button type="button" className="support-btn" onClick={handleEditMessageCancel}>Cancelar</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <p className="support-message__text">{message.text}</p>
                                                            {isOwnMessage && (
                                                                <div className="support-message-actions">
                                                                    <button type="button" className="support-btn" onClick={() => handleEditMessage(message)}>Editar</button>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        <div ref={bottomRef} />
                                    </div>

                                    <form className="support-message-form" onSubmit={handleSendMessage}>
                                        <input
                                            type="text"
                                            value={newMessage}
                                            onChange={(event) => setNewMessage(event.target.value)}
                                            placeholder="Escribe tu respuesta para el usuario..."
                                            disabled={isSending}
                                        />
                                        <button type="submit" disabled={!newMessage.trim() || isSending}>
                                            {isSending ? 'Enviando…' : <Send size={18} />}
                                        </button>
                                    </form>
                                </div>
                            )}

                            {activeDetailTab === 'insight' && (
                                <div className="support-detail-panel support-detail-panel--insight">
                                    <div className="support-user-inspector">
                                        <div className="support-user-inspector__header">
                                            <div>
                                                <h4>Perfil del usuario</h4>
                                                {selectedTicket?.userId && (
                                                    <span className="support-user-inspector__uid">UID: {selectedTicket.userId}</span>
                                                )}
                                            </div>
                                        </div>

                                        {userInsightLoading && (
                                            <div className="support-ticket-placeholder">Cargando datos del usuario...</div>
                                        )}

                                        {userInsightError && (
                                            <div className="support-ticket-error">{userInsightError}</div>
                                        )}

                                        {!userInsightLoading && !userInsightError && userInsight && (
                                            <>
                                                <div className="support-user-inspector__grid">
                                                    {profileFieldConfigs
                                                        .filter((field) => field.always || field.value)
                                                        .map((field) => (
                                                            <div key={field.key}>
                                                                <span className="support-user-inspector__label">{field.label}</span>
                                                                <strong>{field.value || '—'}</strong>
                                                            </div>
                                                        ))}
                                                </div>

                                                <div className="support-user-inspector__metrics">
                                                    <div className="support-user-inspector__metric">
                                                        <span>Total aportado</span>
                                                        <strong>{formatCurrency(insightSummary.totalInvested)}</strong>
                                                    </div>
                                                    <div className="support-user-inspector__metric">
                                                        <span>Acuerdos activos</span>
                                                        <strong>{insightSummary.activeAgreements}/{insightSummary.totalAgreements}</strong>
                                                    </div>
                                                    <div className="support-user-inspector__metric">
                                                        <span>Con acelerador</span>
                                                        <strong>{insightSummary.acceleratorAgreements}</strong>
                                                    </div>
                                                    <div className="support-user-inspector__metric">
                                                        <span>Disponible para retiro</span>
                                                        <strong>{formatCurrency(insightSummary.withdrawableBalance)}</strong>
                                                    </div>
                                                </div>

                                                {hasReferralData && (
                                                    <div className="support-user-inspector__referrals">
                                                        <div className="support-user-inspector__referrals-header">
                                                            <div>
                                                                <h5>Equipo directo</h5>
                                                                <span>{referralSummary.totalDirect ?? 0} referidos</span>
                                                            </div>
                                                            <div>
                                                                <span className="support-user-inspector__label">Activos</span>
                                                                <strong>{referralSummary.withActiveAgreements ?? 0}</strong>
                                                            </div>
                                                        </div>
                                                        {referralList.length === 0 ? (
                                                            <div className="support-ticket-placeholder">Sin detalles de referidos.</div>
                                                        ) : (
                                                            <div className="support-user-inspector__referrals-list">
                                                                {referralList.map((referral) => {
                                                                    const activityDate = referral.lastAgreementAt || referral.createdAt || referral.lastLoginAt;
                                                                    return (
                                                                        <div key={referral.userId} className="support-user-inspector__referral-item">
                                                                            <div>
                                                                                <strong>{referral.displayName || referral.username || 'Sin nombre'}</strong>
                                                                                <span>{referral.username ? `@${referral.username}` : (referral.email || '—')}</span>
                                                                            </div>
                                                                            <div>
                                                                                <span>{referral.activeAgreements} acuerdos activos</span>
                                                                                {activityDate && (
                                                                                    <span>{formatProfileDate(activityDate)}</span>
                                                                                )}
                                                                                {referral.lastAgreementId && (
                                                                                    <span>Último #{referral.lastAgreementId.substring(0, 6)}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {hasCommissionData && (
                                                    <div className="support-user-inspector__commissions">
                                                        <div className="support-user-inspector__commissions-header">
                                                            <div>
                                                                <h5>Comisiones recientes</h5>
                                                                <span>
                                                                    {commissionSummary.lastCommissionAt
                                                                        ? `Última: ${formatDateTime(commissionSummary.lastCommissionAt)}`
                                                                        : 'Sin registros'}
                                                                </span>
                                                            </div>
                                                            <div className="support-user-inspector__commissions-total">
                                                                <span>Total acreditado</span>
                                                                <strong>{formatCurrency(commissionSummary.totalCommissions)}</strong>
                                                            </div>
                                                        </div>
                                                        {(!commissionSummary.recent || commissionSummary.recent.length === 0) ? (
                                                            <div className="support-ticket-placeholder">Sin registros de comisión.</div>
                                                        ) : (
                                                            <div className="support-user-inspector__commissions-list">
                                                                {commissionSummary.recent.map((tx) => (
                                                                    <div key={tx.id} className="support-user-inspector__commission-item">
                                                                        <div>
                                                                            <strong>{formatCurrency(tx.amount)}</strong>
                                                                            <span>{formatDateTime(tx.createdAt)}</span>
                                                                        </div>
                                                                        <div className="support-user-inspector__commission-meta">
                                                                            <span>{tx.sourceAgreementId ? `Acuerdo ${tx.sourceAgreementId.substring(0, 6)}` : 'Sin acuerdo'}</span>
                                                                            <span>{tx.type.replace(/_/g, ' ')}</span>
                                                                            {tx.description && <span>{tx.description}</span>}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="support-user-inspector__agreements">
                                                    <div className="support-user-inspector__agreements-header">
                                                        <h5>Acuerdos recientes</h5>
                                                        <span>{insightAgreements.length} documentos</span>
                                                    </div>
                                                    {insightAgreements.length === 0 ? (
                                                        <div className="support-ticket-placeholder">Sin acuerdos registrados.</div>
                                                    ) : (
                                                        <div className="support-user-inspector__agreements-list">
                                                            {insightAgreements.map((agreement) => (
                                                                <div key={agreement.id} className="support-user-inspector__agreement-item">
                                                                    <div>
                                                                        <strong>#{agreement.id.substring(0, 6)}</strong>
                                                                        <span>{formatProfileDate(agreement.createdAt)}</span>
                                                                    </div>
                                                                    <div className="support-user-inspector__agreement-meta">
                                                                        <span>{formatCurrency(agreement.amount)}</span>
                                                                        <span>{agreement.acceleratorActive ? 'Con acelerador' : 'Sin acelerador'}</span>
                                                                        <span>Progreso: {formatProgressPercent(agreement.progressPercent200)}</span>
                                                                        {commissionAllocations[agreement.id] && (
                                                                            <span>Comisiones: {formatCurrency(commissionAllocations[agreement.id])}</span>
                                                                        )}
                                                                        <StatusBadge status={agreement.status} variant="table" />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Soporte;

/*
Agrega en tu CSS (SoporteAdmin.css):
.support-message-edit-box textarea {
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  font-size: 1rem;
  resize: vertical;
}
.support-message-actions {
  margin-top: 0.3rem;
}
*/
